import { BookOpen, Search, Languages, Layers, User, Users, Compass, Star, Plus } from 'lucide-react';
import type { IconComponent } from './icons';

// Entries in the Home "About" panel. These ship with the app — add or edit them
// here and deploy. Order in `topics` is display order, top to bottom.
//
// Comment threads store their comments in Firestore under
// `forum_topics/{id}/comments`, so a thread's `id` must be STABLE: changing an
// id orphans that thread's existing comments. The other kinds hold no data, so
// their ids are free to change.

/** Never-expiring invite, landing on the server's #intro channel. */
export const DISCORD_INVITE = 'https://discord.gg/pY4rKbGgjC';

interface EntryBase {
  id: string;
  title: string;
  /** Leading icon for the row. Where an entry maps onto a tab, use that tab's
      icon from BottomNav so the two read as the same thing. */
  icon: IconComponent;
}

/** Straight out to an external URL — no in-app screen in between. */
export interface LinkEntry extends EntryBase {
  kind: 'link';
  href: string;
}

/** A read-only page: a paragraph or two, optionally a button leading out. */
export interface InfoEntry extends EntryBase {
  kind: 'info';
  body: string;
  link?: { label: string; href: string };
}

/** One how-to, opened from a TutorialsEntry. */
export interface TutorialEntry {
  id: string;
  title: string;
  icon: IconComponent;
  body: string;
  /** The app's own control, live, under the copy: the reader does the thing
      right there instead of reading how (see GuideAction in Forum.tsx). */
  action: GuideAction;
}

/** Which live control a guide renders. Add a case in GuideAction to add one. */
export type GuideAction =
  /** The Library search box; submitting lands in Library with that search. */
  | 'search'
  /** The position chips from the filter panel; tapping one lands in Library
      filtered to just that position. */
  | 'positions'
  /** The same search box, prompting for a name; a search by athlete. */
  | 'athlete'
  /** Straight to the Discover tab. */
  | 'discover'
  /** The Create Collection chip; naming one lands in Favorites with it open. */
  | 'collection'
  /** The + button: opens the add-technique form. */
  | 'add'
  /** The catalog's other names, listed off the live techniques: every move
      that answers to more than one name, and what it answers to. */
  | 'aliases';

/** A page listing how-tos, each of which opens onto its own page. */
export interface TutorialsEntry extends EntryBase {
  kind: 'tutorials';
  body?: string;
  items: TutorialEntry[];
}

/** The "what's in here" browser: featured creators and positions, listed from
 *  src/data/spotlights.json and counted off the live catalog. Content lives in
 *  that JSON, not here, because the reel generator reads the same file. */
export interface SpotlightsEntry extends EntryBase {
  kind: 'spotlights';
}

/** A commentable discussion thread — the original forum behaviour. */
export interface ForumTopic extends EntryBase {
  kind: 'thread';
  body: string;
}

export type AboutEntry = LinkEntry | InfoEntry | TutorialsEntry | SpotlightsEntry | ForumTopic;

export const topics: AboutEntry[] = [
  {
    kind: 'spotlights',
    id: 'whats-inside',
    title: 'Top Creators',
    icon: Users,
  },
];

/** The everyday name for each of the other names a technique can carry. The
 *  names themselves live on the techniques in Firestore (`japaneseName` and
 *  `aliases`) — that's what search actually matches on; this is only what to
 *  call the move in the "Other names we match" list, so it reads as a pair of
 *  names ("Armbar" / "Juji Gatame") rather than a clip title. Keyed
 *  lowercase. Anything not in here falls back to the clip's own name, so a
 *  new alias still shows up — add it here to give it a proper label. */
export const LINGO_TERMS: Record<string, string> = {
  'juji gatame': 'Armbar',
  'sankaku jime': 'Triangle choke',
  'omoplata': 'Shoulder lock',
  'tarikoplata': 'Wrist crush shoulder lock',
  'kimura': 'Reverse arm lock',
  'berimbolo': 'Inversion back take',
  'de la riva': 'DLR guard',
  'hadaka jime': 'Rear naked choke',
  'mae hadaka jime': 'Guillotine',
  'seoi nage': 'Shoulder throw',
  'ouchi gari': 'Big inner reap',
  'kouchi gari': 'Small inner reap',
  'osoto gari': 'Large outer reap',
  'de ashi barai': 'Foot sweep',
  'sasae tsurikomi ashi': 'Propping ankle block',
  'harai goshi': 'Sweeping hip throw',
  'uchi mata': 'Inner thigh throw',
  'tani otoshi': 'Valley drop',
  'hasami gaeshi': 'Scissor sweep',
  'mae ukemi': 'Forward roll',
  'kote gaeshi': 'Wrist lock',
  'hiza gatame': 'Knee lock',
  "fireman's carry": 'Kata guruma',
};

/** The How To screen: opened from the ? button in Home's header rather than
 *  listed with the topics, so Home's body stays content. */
export const HOW_TO: TutorialsEntry = {
  kind: 'tutorials',
  id: 'how-to-use',
  title: 'How To',
  icon: BookOpen,
  items: [
    {
      id: 'find-again',
      title: "I saw a technique and can't find it",
      icon: Search,
      body:
        'Search matches the technique name, the position it comes from and whoever posted ' +
        'it, so any one of those is enough to get back to it. Type what you remember.',
      action: 'search',
    },
    {
      id: 'lingo',
      title: 'I call it something different',
      icon: Languages,
      body:
        'Say it the way your gym says it. Every name in a row finds the same clips, so ' +
        'honey hole, 411 and inside sankaku all land on the saddle. Tap any name to run ' +
        'it as a search.',
      action: 'aliases',
    },
    {
      id: 'study-position',
      title: 'I want to get good at one guard',
      icon: Layers,
      body:
        'Pick a position and stay there. Library narrows to just that guard; star what is ' +
        'worth drilling and it lands in Favorites.',
      action: 'positions',
    },
    {
      id: 'by-creator',
      title: 'I want everything from one athlete',
      icon: User,
      body:
        'Search matches who posted a clip and who is in it, as well as the technique name, ' +
        'so a name or an @handle pulls up everything of theirs in the catalog.',
      action: 'athlete',
    },
    {
      id: 'discover',
      title: 'I want to see something new',
      icon: Compass,
      body:
        'Discover is a swipe feed: one technique at a time, swipe up for the next. It skips ' +
        'anything you have already starred or marked done, so it stays fresh as you work ' +
        'through the catalog.',
      action: 'discover',
    },
    {
      id: 'favorites',
      title: 'I want a list to practice',
      icon: Star,
      body:
        'Star a technique to send it to Favorites. There you can group starred techniques ' +
        'into collections: one per position, per gameplan, or per week of drilling. Name ' +
        'one here, then drag techniques onto it.',
      action: 'collection',
    },
    {
      id: 'submitting',
      title: "I found a clip that isn't in here",
      icon: Plus,
      body:
        'Paste a social media link and it is saved to your own library straight away. It ' +
        'also goes to the review queue, and approved submissions get added to the catalog ' +
        'for everyone. The catalog is picky on purpose: one technique per clip, no ' +
        'compilations or highlight reels. Anything else still lives in your own library, ' +
        'it just does not go public.',
      action: 'add',
    },
  ],
};
